import is from '@sindresorhus/is';
import { isApiResponseError } from '@ui/util/api-response-errors';
import { isAxiosError } from 'axios';
import { FieldError, FieldErrors, FieldValues } from 'react-hook-form';
import type { $ZodErrorTree } from 'zod/v4/core';

interface NormalizedError {
  title: string;
  description: string | undefined;
  validationErrors?: $ZodErrorTree<unknown>;
}

type ValidationSummaryEntry = {
  path: string;
  message: string;
};

/**
 * Extract error message data from various API error response formats.
 *
 * @remarks
 *
 * Presently, our API responses may include error information in different
 * shapes because of inconsistencies in how errors are thrown and handled across
 * our backend codebase (legacy endpoints).
 *
 *
 * This function handles multiple potential shapes of error responses, including:
 * - { error: { message: string }, message?: string, detail?: string }
 * - { error: string, message?: string, detail?: string }
 * - { message?: string, detail?: string }
 *
 * It also accounts for the possibility of validation errors included in the
 * response.
 */
function getApiErrorMessageData(data: unknown): {
  title?: string;
  description?: string;
  validationErrors?: $ZodErrorTree<unknown>;
} {
  if (!is.plainObject(data)) {
    return {};
  }

  const message = data.message;
  const detail = data.detail;
  const error = data.error;
  const validationErrors = data.validationErrors;

  if (is.plainObject(error) && is.string(error.message)) {
    return {
      title: error.message,
      description: is.string(detail)
        ? detail
        : is.string(message)
          ? message
          : undefined,
      validationErrors: validationErrors as $ZodErrorTree<unknown> | undefined,
    };
  }

  if (is.string(error)) {
    return {
      title: is.string(message) ? message : error,
      description: is.string(detail) ? detail : undefined,
      validationErrors: validationErrors as $ZodErrorTree<unknown> | undefined,
    };
  }

  return {
    title: is.string(message) ? message : undefined,
    description: is.string(detail) ? detail : undefined,
    validationErrors: validationErrors as $ZodErrorTree<unknown> | undefined,
  };
}

function isFetchApiError(
  error: unknown,
): error is { status: number; data: unknown } {
  return (
    is.plainObject(error) &&
    'status' in error &&
    is.number(error.status) &&
    'data' in error
  );
}

export function normalizeError(
  error: unknown,
  defaultTitle: string,
): NormalizedError | null {
  if (!error) {
    return null;
  }

  if (isApiResponseError(error)) {
    const { title, description, validationErrors } = getApiErrorMessageData(
      error.data,
    );

    return {
      title: title ?? defaultTitle,
      description,
      validationErrors,
    };
  }

  if (isFetchApiError(error)) {
    const { title, description, validationErrors } = getApiErrorMessageData(
      error.data,
    );

    return {
      title: title ?? defaultTitle,
      description,
      validationErrors,
    };
  }

  if (isAxiosError(error)) {
    const { title, description, validationErrors } = getApiErrorMessageData(
      error.response?.data,
    );

    return {
      title: title ?? defaultTitle,
      description,
      validationErrors,
    };
  }

  if (is.error(error)) {
    return {
      title: error.message || defaultTitle,
      description: undefined,
    };
  }

  if (is.plainObject(error) && is.string(error.message)) {
    return {
      title: error.message || defaultTitle,
      description: undefined,
    };
  }

  return {
    title: defaultTitle,
    description: undefined,
  };
}

function collectValidationSummaryEntries(
  tree: unknown,
  pathSegments: string[],
  output: ValidationSummaryEntry[],
) {
  if (!is.plainObject(tree)) {
    return;
  }

  const errors = tree.errors;
  if (Array.isArray(errors)) {
    const path = pathSegments.length > 0 ? pathSegments.join('.') : 'General';

    for (const entry of errors) {
      if (is.string(entry) && entry.trim().length > 0) {
        output.push({ path, message: entry });
      }
    }
  }

  const properties = tree.properties;
  if (is.plainObject(properties)) {
    for (const [key, value] of Object.entries(properties)) {
      collectValidationSummaryEntries(value, [...pathSegments, key], output);
    }
  }

  const items = tree.items;
  if (Array.isArray(items)) {
    for (const [index, value] of items.entries()) {
      if (!value) {
        continue;
      }

      collectValidationSummaryEntries(
        value,
        [...pathSegments, `[${index}]`],
        output,
      );
    }
  }
}

export function summarizeValidationErrors(
  tree: $ZodErrorTree<unknown> | undefined,
): string[] {
  if (!tree) {
    return [];
  }

  const summaryEntries: ValidationSummaryEntry[] = [];

  collectValidationSummaryEntries(tree, [], summaryEntries);

  const seen = new Set<string>();
  const uniqueSummaries: string[] = [];

  for (const entry of summaryEntries) {
    const line =
      entry.path === 'General'
        ? entry.message
        : `${entry.path}: ${entry.message}`;

    if (!seen.has(line)) {
      seen.add(line);
      uniqueSummaries.push(line);
    }
  }

  return uniqueSummaries;
}

function isFieldError(value: unknown): value is FieldError {
  return is.plainObject(value) && ('message' in value || 'type' in value);
}

function isErrorRecord(value: unknown): value is Record<string, unknown> {
  return is.plainObject(value) && !isFieldError(value);
}

/**
 * Flatten a nested react-hook-form errors object into a single-level
 * mapping of field paths to FieldError objects.
 */
export function flattenErrors<T extends FieldValues>(
  errors: FieldErrors<T>,
): Record<string, FieldError> {
  const result: Record<string, FieldError> = {};

  function recurse(current: Record<string, unknown>, prefix: string) {
    for (const key in current) {
      const value = current[key];
      const newPrefix = prefix ? `${prefix}.${key}` : key;

      if (isFieldError(value)) {
        // This is a field error, store it
        result[newPrefix] = value;
      } else if (Array.isArray(value)) {
        // Handle field arrays - iterate through array indices
        value.forEach((item, index) => {
          if (isErrorRecord(item)) {
            recurse(item, `${newPrefix}.${index}`);
          }
        });
      } else if (isErrorRecord(value)) {
        // Handle nested objects
        recurse(value, newPrefix);
      }
    }
  }

  recurse(errors, '');

  return result;
}
